import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sidebar } from '@/components/Sidebar';
import { CARD_THEMES, THEME_LIST } from '@/components/cardThemes';
import { generateGallery, getCoverDataURL } from '@/components/coverGenerator';
import { getAICovers, getAllAICovers, getAICoverUrl } from '@/components/aiCovers';
import { Save, ExternalLink, Upload, Eye, Plus, Trash2, Image, HelpCircle, Star, Palette, Lock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = API.replace('/api', '');

const EMPTY_PROFILE = {
  id: 'primary', title: 'Основная визитка', display_name: '', slug: '', bio: '',
  specialization: '', tags: [], contacts: {}, show_prices: true,
  show_contacts: true, accepting_clients: true, bookable_formats: [], avatar: '',
  cover: '', certificates: [], faq: [], reviews: [], card_theme: 'forest',
  cover_type: 'upload', cover_pattern: '', booking_note: '',
};

export default function ProfileEditPage() {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [profiles, setProfiles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [plan, setPlan] = useState('start');
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedSlug, setSavedSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [coverGalleryOpen, setCoverGalleryOpen] = useState(false);
  const [profileRules, setProfileRules] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    Promise.all([
      api.get(`/profiles`),
      api.get(`/settings`),
      api.get(`/subscription`),
    ]).then(([pRes, sRes, subRes]) => {
      if (Array.isArray(pRes.data) && pRes.data.length) {
        const loaded = pRes.data.map(item => ({ ...EMPTY_PROFILE, ...item }));
        setProfiles(loaded);
        setProfile(loaded[0]);
        setSavedSlug(loaded[0].slug || '');
      }
      setSettings(sRes.data);
      setPlan(subRes.data.plan?.id || 'start');
    }).catch(err => { if (err.response?.status === 401) navigate('/login'); })
      .finally(() => setLoading(false));
    // Load profile rules (for clinic doctors)
    api.get('/doctor-profile-rules').then(({ data }) => setProfileRules(data)).catch(() => {});
  }, [navigate]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/profiles/${profile.id || 'primary'}`, {
        title: profile.title, display_name: profile.display_name,
        slug: profile.slug, bio: profile.bio, specialization: profile.specialization,
        tags: profile.tags, contacts: profile.contacts, show_prices: profile.show_prices,
        show_contacts: profile.show_contacts, accepting_clients: profile.accepting_clients,
        bookable_formats: profile.bookable_formats, faq: profile.faq, reviews: profile.reviews,
        card_theme: profile.card_theme, cover_type: profile.cover_type, cover_pattern: profile.cover_pattern,
        booking_note: profile.booking_note,
      });
      const saved = { ...EMPTY_PROFILE, ...data };
      setProfile(saved);
      setProfiles(current => current.map(item => item.id === saved.id ? saved : item));
      setSavedSlug(saved.slug || '');
      toast.success('Визитка сохранена');
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    setSaving(false);
  };

  const selectProfile = (profileId) => {
    const selected = profiles.find(item => item.id === profileId);
    if (!selected) return;
    setProfile({ ...EMPTY_PROFILE, ...selected });
    setSavedSlug(selected.slug || '');
    setTagInput('');
  };

  const createProfile = async () => {
    try {
      const { data } = await api.post('/profiles', {
        copy_from_id: profile.id || 'primary',
        title: `Визитка ${profiles.length + 1}`,
      });
      const created = { ...EMPTY_PROFILE, ...data };
      setProfiles(current => [...current, created]);
      setProfile(created);
      setSavedSlug('');
      toast.success('Новая визитка создана на основе текущей');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось создать визитку');
    }
  };

  const deleteProfile = async () => {
    if (profile.id === 'primary' || !window.confirm(`Удалить визитку «${profile.title}»?`)) return;
    try {
      await api.delete(`/profiles/${profile.id}`);
      const remaining = profiles.filter(item => item.id !== profile.id);
      setProfiles(remaining);
      setProfile({ ...EMPTY_PROFILE, ...remaining[0] });
      setSavedSlug(remaining[0]?.slug || '');
      toast.success('Визитка удалена');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось удалить визитку');
    }
  };

  const isPaid = plan !== 'start';
  const isProfi = ['profi', 'clinic'].includes(plan);
  const theme = CARD_THEMES[profile.card_theme] || CARD_THEMES.forest;
  const gallery = generateGallery(theme.accent, theme.bg, 3);

  // Owner's rules for clinic doctors (null = no restrictions / solo practitioner)
  const r = profileRules || {};
  const canShowContacts = r.allow_contacts !== false;
  const canShowBio = r.allow_bio !== false;
  const canShowReviews = r.allow_reviews !== false;
  const canShowFaq = r.allow_faq !== false;
  const canShowCerts = r.allow_certificates !== false;
  const isClinicDoctor = !!r.allow_contacts !== undefined && profileRules !== null;

  const Section = ({ title, icon: Icon, requiresPlan, children }) => {
    const locked = requiresPlan === 'practic' ? !isPaid : requiresPlan === 'profi' ? !isProfi : false;
    if (locked) return null;
    return (
      <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
        <CardContent className="p-4">
          {Icon && <div className="flex items-center gap-1.5 mb-2"><Icon size={14} style={{ color: 'var(--app-text-muted)' }} /><span className="text-sm font-medium" style={{ color: 'var(--app-text-secondary)' }}>{title}</span></div>}
          {children}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 animate-fade-in">
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl" style={{ color: 'var(--app-text)' }}>Визитки</h1>
              <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>Отдельная страница и памятка для каждого канала работы.</p>
            </div>
            <div className="flex gap-2">
              {savedSlug && savedSlug === profile.slug && <a href={`/p/${savedSlug}`} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}><Eye size={14} className="mr-1.5" /> Предпросмотр</Button></a>}
              <Button size="sm" onClick={saveProfile} disabled={saving} className="text-white" style={{ background: 'var(--app-accent)' }}><Save size={14} className="mr-1.5" /> {saving ? '...' : 'Сохранить'}</Button>
            </div>
          </div>

          {loading ? <p style={{ color: 'var(--app-text-muted)' }} className="text-center py-12">Загрузка...</p> : (
            <div className="space-y-4 animate-fade-in delay-1">
              <div className="flex items-center gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Ваши визитки">
                {profiles.map(item => {
                  const active = item.id === profile.id;
                  return (
                    <button key={item.id} type="button" onClick={() => selectProfile(item.id)}
                      className="shrink-0 rounded-lg border px-3 py-2 text-left transition-colors"
                      style={{
                        background: active ? 'var(--app-accent-muted)' : 'var(--app-card)',
                        borderColor: active ? 'var(--app-accent)' : 'var(--app-border)',
                        color: active ? 'var(--app-text)' : 'var(--app-text-secondary)',
                      }}>
                      <span className="block text-sm font-medium">{item.title || 'Визитка'}</span>
                      <span className="block text-[11px] mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                        {item.slug ? `/p/${item.slug}` : 'адрес не задан'}
                      </span>
                    </button>
                  );
                })}
                <Button type="button" size="sm" variant="outline" onClick={createProfile}
                  className="shrink-0 h-auto py-2" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}>
                  <Plus size={14} className="mr-1.5" /> Добавить визитку
                </Button>
              </div>

              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--app-text-secondary)' }}>Название внутри сервиса</label>
                      <Input value={profile.title || ''} onChange={e => setProfile(p => ({ ...p, title: e.target.value }))}
                        style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="Например, Личная практика" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--app-text-secondary)' }}>Имя или название на странице</label>
                      <Input value={profile.display_name || ''} onChange={e => setProfile(p => ({ ...p, display_name: e.target.value }))}
                        style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder={settings?.psychologist_name || 'Публичное название'} />
                    </div>
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--app-text-muted)' }}>Внутреннее название помогает не перепутать каналы. Публичное можно оставить пустым — тогда покажем имя аккаунта.</p>
                  {profile.id !== 'primary' && (
                    <Button type="button" variant="ghost" size="sm" onClick={deleteProfile}
                      className="mt-2 px-0 text-red-400 hover:text-red-300"><Trash2 size={14} className="mr-1.5" />Удалить эту визитку</Button>
                  )}
                </CardContent>
              </Card>

              {/* Theme picker */}
              <Section title="Тема оформления" icon={Palette} requiresPlan="practik">
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {THEME_LIST.map(t => (
                    <button key={t.id} onClick={() => setProfile(p => ({ ...p, card_theme: t.id }))}
                      className={`rounded-lg p-1 border-2 transition-all ${profile.card_theme === t.id ? 'scale-105' : 'opacity-70 hover:opacity-100'}`}
                      style={{ borderColor: profile.card_theme === t.id ? t.accent : 'transparent' }} title={t.name}>
                      <div className="h-8 rounded" style={{ background: t.bg }}>
                        <div className="w-3 h-3 rounded-full mx-auto mt-2.5" style={{ background: t.accent }} />
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--app-text-faint)' }}>Текущая: {theme.name}</p>
              </Section>

              {/* Cover */}
              <Section title="Обложка" icon={Image}>
                <div className="flex gap-2 mb-3">
                  <Button size="sm" className="text-xs h-7"
                    onClick={() => setCoverGalleryOpen(true)}
                    style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)', border: '1px solid var(--app-accent)' }}>
                    Выбрать из галереи
                  </Button>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept="image/*" onChange={e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('file', file);
                      api.put(`/profile/cover?profile_id=${encodeURIComponent(profile.id || 'primary')}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                        .then(({ data }) => { setProfile(p => ({ ...p, cover: data.cover, cover_type: 'upload', cover_pattern: '' })); toast.success('Загружено'); }).catch(() => toast.error('Ошибка'));
                    }} />
                    <span className="inline-flex items-center h-7 px-3 rounded-md text-xs cursor-pointer" style={{ background: 'var(--app-card-deep)', color: 'var(--app-text-muted)', border: '1px solid var(--app-border)' }}>
                      <Upload size={12} className="mr-1" />Загрузить свою
                    </span>
                  </label>
                </div>
                {/* Preview current cover */}
                {profile.cover_type === 'upload' && profile.cover && (
                  <div className="rounded-lg overflow-hidden"><img src={`${BASE}/api/covers/${profile.cover}`} alt="" className="w-full" /></div>
                )}
                {profile.cover_type === 'ai' && profile.cover_pattern?.startsWith('ai:') && (
                  <div className="rounded-lg overflow-hidden"><img src={getAICoverUrl(profile.cover_pattern.replace('ai:', ''))} alt="" className="w-full" /></div>
                )}
                {profile.cover_type === 'generated' && profile.cover_pattern?.startsWith('svg:') && (
                  <div className="rounded-lg overflow-hidden"><img src={getCoverDataURL(profile.cover_pattern.split(':')[1], 1200, 300, theme.accent, theme.bg, parseInt(profile.cover_pattern.split(':')[2]) || 42)} alt="" className="w-full" /></div>
                )}
                {!profile.cover && !profile.cover_pattern && (
                  <div className="h-20 rounded-lg border-2 border-dashed flex items-center justify-center" style={{ borderColor: 'var(--app-border)' }}>
                    <span className="text-xs" style={{ color: 'var(--app-text-faint)' }}>Нет обложки</span>
                  </div>
                )}
              </Section>

              {/* Avatar + Slug */}
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 border-2" style={{ borderColor: 'var(--app-border)', background: 'var(--app-card-deep)' }}>
                      {profile.avatar ? <img src={`${BASE}/api/avatars/${profile.avatar}`} alt="" className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center"><span className="text-2xl font-heading" style={{ color: 'var(--app-accent)' }}>{(profile.display_name || settings?.psychologist_name || '?')[0]}</span></div>}
                    </div>
                    <div className="flex-1">
                      <p className="font-heading text-base" style={{ color: 'var(--app-text)' }}>{profile.display_name || settings?.psychologist_name}</p>
                      <label className="cursor-pointer mt-1 inline-block"><input type="file" className="hidden" accept="image/*" onChange={e => {
                        const file = e.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file);
                        api.put(`/profile/avatar?profile_id=${encodeURIComponent(profile.id || 'primary')}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                          .then(({ data }) => { setProfile(p => ({ ...p, avatar: data.avatar })); toast.success('Фото загружено'); }).catch(() => toast.error('Ошибка'));
                      }} /><span className="text-xs px-2 py-1 rounded cursor-pointer" style={{ background: 'var(--app-card-deep)', color: 'var(--app-text-muted)' }}><Upload size={12} className="inline mr-1" />Фото</span></label>
                    </div>
                  </div>
                  <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--app-text-secondary)' }}>Адрес визитки</label>
                  <div className="flex items-center">
                    <span className="text-xs px-2 py-1.5 rounded-l border border-r-0" style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>/p/</span>
                    <Input value={profile.slug || ''} onChange={e => setProfile(p => ({ ...p, slug: e.target.value.replace(/[^a-z0-9_-]/gi, '').toLowerCase() }))}
                      className="rounded-l-none text-sm" style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="ivanova" />
                  </div>
                  <p className="text-xs mt-1.5" style={{ color: 'var(--app-text-muted)' }}>
                    Придумайте короткий адрес латиницей. После сохранения сверху появится кнопка предпросмотра.
                  </p>
                  {profile.slug && profile.slug !== savedSlug && (
                    <p className="text-xs mt-1" style={{ color: 'var(--app-accent)' }}>Сохраните визитку, чтобы адрес /p/{profile.slug} начал работать.</p>
                  )}
                </CardContent>
              </Card>

              {/* Spec + Bio */}
              {canShowBio && (
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4 space-y-3">
                  <Input value={profile.specialization || ''} onChange={e => setProfile(p => ({ ...p, specialization: e.target.value }))}
                    style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="Специализация" />
                  <textarea value={profile.bio || ''} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                    className="w-full min-h-[80px] rounded-md text-sm p-3 resize-y border" style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="О себе..." />
                </CardContent>
              </Card>
              )}

              {/* Tags */}
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4">
                  <span className="text-sm font-medium" style={{ color: 'var(--app-text-secondary)' }}>Направления</span>
                  <div className="flex flex-wrap gap-1.5 my-2">
                    {(profile.tags || []).map((tag, i) => (
                      <Badge key={i} className="text-xs px-2 py-0.5" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>{tag}<button onClick={() => setProfile(p => ({ ...p, tags: p.tags.filter((_, j) => j !== i) }))} className="ml-1">&times;</button></Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim()) { setProfile(p => ({ ...p, tags: [...(p.tags||[]), tagInput.trim()] })); setTagInput(''); } } }}
                      style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="Добавить тег..." className="text-sm" />
                  </div>
                </CardContent>
              </Card>

              {/* Certificates (paid only) */}
              {canShowCerts && (
              <Section title="Дипломы и сертификаты" icon={Image} requiresPlan="practik">
                <label className="cursor-pointer float-right -mt-6"><input type="file" className="hidden" accept="image/*" onChange={e => {
                  const file = e.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file);
                  api.post(`/profile/certificates?profile_id=${encodeURIComponent(profile.id || 'primary')}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                    .then(({ data }) => { setProfile(p => ({ ...p, certificates: data.certificates })); toast.success('Добавлен'); }).catch(() => toast.error('Ошибка'));
                }} /><span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}><Plus size={12} className="inline mr-1" />Добавить</span></label>
                {(profile.certificates || []).length > 0 ? (
                  <div className="flex gap-2 overflow-x-auto pb-1 mt-1">
                    {profile.certificates.map((c, i) => (
                      <div key={i} className="relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--app-border)' }}>
                        <img src={`${BASE}/api/certificates/${c}`} alt="" className="w-full h-full object-cover" />
                        <button onClick={async () => { await api.delete(`/profile/certificates/${c}?profile_id=${encodeURIComponent(profile.id || 'primary')}`); setProfile(p => ({ ...p, certificates: p.certificates.filter(x => x !== c) })); }}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs" style={{ background: 'rgba(0,0,0,0.6)' }}>&times;</button>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs mt-1" style={{ color: 'var(--app-text-faint)' }}>Нет сертификатов</p>}
              </Section>
              )}

              {/* FAQ (paid only) */}
              {canShowFaq && (
              <Section title="FAQ" icon={HelpCircle} requiresPlan="practik">
                <Button size="sm" variant="ghost" onClick={() => setProfile(p => ({ ...p, faq: [...(p.faq||[]), { q: '', a: '', _key: Date.now() }] }))} style={{ color: 'var(--app-accent)' }} className="text-xs h-7 float-right -mt-6"><Plus size={12} className="mr-1" />Добавить</Button>
                <div className="space-y-2 mt-1">
                  {(profile.faq || []).map((item, i) => (
                    <div key={item._key || `faq-${i}`} className="p-3 rounded-lg border space-y-2" style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)' }}>
                      <div className="flex gap-2"><Input defaultValue={item.q} onBlur={e => { const f = [...profile.faq]; f[i] = { ...f[i], q: e.target.value }; setProfile(p => ({ ...p, faq: f })); }} className="text-sm" style={{ background: 'transparent', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="Вопрос" />
                        <button onClick={() => setProfile(p => ({ ...p, faq: p.faq.filter((_, j) => j !== i) }))} style={{ color: 'var(--app-text-faint)' }}><Trash2 size={14} /></button></div>
                      <textarea defaultValue={item.a} onBlur={e => { const f = [...profile.faq]; f[i] = { ...f[i], a: e.target.value }; setProfile(p => ({ ...p, faq: f })); }}
                        className="w-full min-h-[40px] rounded text-xs p-2 resize-y border" style={{ background: 'transparent', borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }} placeholder="Ответ" />
                    </div>
                  ))}
                </div>
              </Section>
              )}

              {/* Reviews (profi only) */}
              {canShowReviews && (
              <Section title="Отзывы" icon={Star} requiresPlan="profi">
                <Button size="sm" variant="ghost" onClick={() => setProfile(p => ({ ...p, reviews: [...(p.reviews||[]), { text: '', author: '', _key: Date.now() }] }))} style={{ color: 'var(--app-accent)' }} className="text-xs h-7 float-right -mt-6"><Plus size={12} className="mr-1" />Добавить</Button>
                <div className="space-y-2 mt-1">
                  {(profile.reviews || []).map((r, i) => (
                    <div key={r._key || `rev-${i}`} className="p-3 rounded-lg border space-y-2" style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)' }}>
                      <div className="flex gap-2"><Input defaultValue={r.author} onBlur={e => { const rv = [...profile.reviews]; rv[i] = { ...rv[i], author: e.target.value }; setProfile(p => ({ ...p, reviews: rv })); }}
                        className="text-sm w-1/3" style={{ background: 'transparent', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="Автор" />
                        <button onClick={() => setProfile(p => ({ ...p, reviews: p.reviews.filter((_, j) => j !== i) }))} style={{ color: 'var(--app-text-faint)' }}><Trash2 size={14} /></button></div>
                      <textarea defaultValue={r.text} onBlur={e => { const rv = [...profile.reviews]; rv[i] = { ...rv[i], text: e.target.value }; setProfile(p => ({ ...p, reviews: rv })); }}
                        className="w-full min-h-[40px] rounded text-xs p-2 resize-y border" style={{ background: 'transparent', borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }} placeholder="Текст отзыва" />
                      {/* Photo attachment */}
                      <div className="flex items-center gap-2">
                        {r.photo && (
                          <div className="relative w-16 h-16 rounded overflow-hidden border" style={{ borderColor: 'var(--app-border)' }}>
                            <img src={r.photo.startsWith('http') ? r.photo : `/api/uploads/reviews/${r.photo}`} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => { const rv = [...profile.reviews]; rv[i] = { ...rv[i], photo: '' }; setProfile(p => ({ ...p, reviews: rv })); }}
                              className="absolute top-0 right-0 bg-red-600/80 text-white rounded-bl p-0.5"><X size={8} /></button>
                          </div>
                        )}
                        <label className="cursor-pointer text-xs px-2 py-1 rounded border transition-colors hover:opacity-80" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>
                          <input type="file" accept="image/*" className="hidden" onChange={async e => {
                            const file = e.target.files?.[0]; if (!file) return;
                            const fd = new FormData(); fd.append('file', file);
                            try {
                              const { data } = await api.post('/profile/review-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              const rv = [...profile.reviews]; rv[i] = { ...rv[i], photo: data.filename }; setProfile(p => ({ ...p, reviews: rv }));
                            } catch { toast.error('Ошибка загрузки фото'); }
                          }} />
                          {r.photo ? 'Заменить фото' : '+ Фото'}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
              )}

              {/* Contacts + Booking */}
              {canShowContacts && (
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium" style={{ color: 'var(--app-text-secondary)' }}>Контакты</span>
                    <label className="flex items-center gap-2"><span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Показывать</span><Switch checked={profile.show_contacts !== false} onCheckedChange={v => setProfile(p => ({ ...p, show_contacts: v }))} /></label></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input value={(profile.contacts || {}).telegram || ''} onChange={e => setProfile(p => ({ ...p, contacts: { ...p.contacts, telegram: e.target.value } }))} style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="Telegram" className="text-sm" />
                    <Input value={(profile.contacts || {}).whatsapp || ''} onChange={e => setProfile(p => ({ ...p, contacts: { ...p.contacts, whatsapp: e.target.value } }))} style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} placeholder="WhatsApp" className="text-sm" />
                  </div>
                </CardContent>
              </Card>
              )}

              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4 space-y-3">
                  <span className="text-sm font-medium" style={{ color: 'var(--app-text-secondary)' }}>Запись</span>
                  <label className="flex items-center justify-between cursor-pointer">
                    <div><p className="text-sm" style={{ color: 'var(--app-text)' }}>Принимаю новых клиентов</p></div>
                    <Switch checked={profile.accepting_clients !== false} onCheckedChange={v => setProfile(p => ({ ...p, accepting_clients: v }))} />
                  </label>
                  <div><p className="text-xs mb-1.5" style={{ color: 'var(--app-text-muted)' }}>Форматы для записи:</p>
                    {(settings?.meeting_formats || []).map(fmt => {
                      const on = !(profile.bookable_formats || []).length || (profile.bookable_formats || []).includes(fmt.id);
                      return <label key={fmt.id} className="flex items-center gap-2 p-2 rounded cursor-pointer mb-1" style={{ background: on ? 'var(--app-accent-muted)' : 'transparent' }}>
                        <input type="checkbox" checked={on} onChange={() => { const all = settings.meeting_formats.map(f => f.id); const cur = (profile.bookable_formats || []).length === 0 ? [...all] : [...profile.bookable_formats]; const next = cur.includes(fmt.id) ? cur.filter(id => id !== fmt.id) : [...cur, fmt.id]; setProfile(p => ({ ...p, bookable_formats: next.length === all.length ? [] : next })); }} style={{ accentColor: 'var(--app-accent)' }} />
                        <span className="text-sm" style={{ color: 'var(--app-text)' }}>{fmt.name}</span><span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{fmt.duration} мин.</span>
                      </label>;
                    })}
                  </div>
                  <label className="flex items-center justify-between cursor-pointer"><span className="text-sm" style={{ color: 'var(--app-text)' }}>Показывать цены</span><Switch checked={profile.show_prices !== false} onCheckedChange={v => setProfile(p => ({ ...p, show_prices: v }))} /></label>
                  <div className="pt-3 border-t" style={{ borderColor: 'var(--app-border)' }}>
                    <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--app-text-secondary)' }}>Памятка перед записью</label>
                    <textarea value={profile.booking_note || ''} onChange={e => setProfile(p => ({ ...p, booking_note: e.target.value }))}
                      className="w-full min-h-[88px] rounded-md text-sm p-3 resize-y border"
                      style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
                      placeholder="Например: после записи напишите мне в Telegram или укажите удобный способ связи в примечании." />
                    <p className="text-xs mt-1.5" style={{ color: 'var(--app-text-muted)' }}>Текст увидят только посетители этой визитки.</p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end pt-2 pb-8">
                <Button onClick={saveProfile} disabled={saving} className="text-white" style={{ background: 'var(--app-accent)' }}><Save size={16} className="mr-2" /> {saving ? '...' : 'Сохранить визитку'}</Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Cover Gallery Dialog */}
      <Dialog open={coverGalleryOpen} onOpenChange={setCoverGalleryOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Выберите обложку</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1">
            {/* AI covers for current theme */}
            <p className="text-xs mb-2" style={{ color: 'var(--app-text-muted)' }}>Под вашу тему ({theme.name})</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              {getAICovers(profile.card_theme).map(c => {
                const active = profile.cover_pattern === `ai:${c.id}`;
                return (
                  <button key={c.id} onClick={() => { setProfile(p => ({ ...p, cover_type: 'ai', cover_pattern: `ai:${c.id}` })); setCoverGalleryOpen(false); }}
                    className={`rounded-lg overflow-hidden border-2 transition-all ${active ? 'ring-2' : 'hover:opacity-90'}`}
                    style={{ borderColor: active ? theme.accent : 'var(--app-border)' }}>
                    <img src={c.url} alt={c.name} className="w-full h-24 object-cover" />
                    <p className="text-[10px] py-1 text-center" style={{ background: 'var(--app-card)', color: 'var(--app-text-muted)' }}>{c.name}</p>
                  </button>
                );
              })}
            </div>
            {/* Other themes */}
            <p className="text-xs mb-2" style={{ color: 'var(--app-text-faint)' }}>Все темы</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              {getAllAICovers().filter(c => c.theme !== profile.card_theme).map(c => {
                const active = profile.cover_pattern === `ai:${c.id}`;
                return (
                  <button key={c.id} onClick={() => { setProfile(p => ({ ...p, cover_type: 'ai', cover_pattern: `ai:${c.id}` })); setCoverGalleryOpen(false); }}
                    className={`rounded-lg overflow-hidden border-2 transition-all ${active ? 'ring-2' : 'hover:opacity-90'}`}
                    style={{ borderColor: active ? theme.accent : 'var(--app-border)' }}>
                    <img src={c.url} alt={c.name} className="w-full h-20 object-cover" />
                    <p className="text-[10px] py-1 text-center" style={{ background: 'var(--app-card)', color: 'var(--app-text-faint)' }}>{c.name}</p>
                  </button>
                );
              })}
            </div>
            {/* SVG fallback */}
            <p className="text-xs mb-2" style={{ color: 'var(--app-text-faint)' }}>Паттерны (SVG)</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {gallery.map((g, i) => {
                const key = `svg:${g.patternId}:${g.seed}`;
                const active = profile.cover_pattern === key;
                return (
                  <button key={i} onClick={() => { setProfile(p => ({ ...p, cover_type: 'generated', cover_pattern: key })); setCoverGalleryOpen(false); }}
                    className={`rounded-lg overflow-hidden border transition-all ${active ? 'ring-1' : 'hover:opacity-90'}`}
                    style={{ borderColor: active ? theme.accent : 'var(--app-border)' }}>
                    <img src={getCoverDataURL(g.patternId, 400, 150, theme.accent, theme.bg, g.seed)} alt={g.name} className="w-full h-14 object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
